import {AppUrls} from 'shared/config/model/urls';

import {MouseEvent, useCallback} from 'react';
import {generatePath, useNavigate} from 'react-router-dom';

import {MonitorRowClickHandler, useMonitorRowSelection, useMonitorRowState} from 'entities/monitor';
import {getHttpMonitorDisplayName, HttpMonitorModalType} from 'features/http/lib/httpMonitorView';
import useToggleTagFilter from 'shared/lib/hooks/useToggleTagFilter';
import {YM_GOALS} from 'shared/lib/analytics';

import ITag from 'shared/model/ITags';

import IHttpMonitorOutput from './HttpMonitor/IHttpMonitor';
import {httpMonitorSelectedIdsSelector, toggleOne} from './httpMonitorSlice';
import useCanCreateHttpMonitor from './useCanCreateHttpMonitor';
import {useHttpMonitorCheckNow} from './useHttpMonitorMutations';
import useHttpMonitorToggle from './useHttpMonitorToggle';

export interface IUseHttpMonitorRow {
    activeModal: HttpMonitorModalType,
    closeModal: () => void,
    canCreate: boolean,
    isSelected: boolean,
    isPaused: boolean,
    tags: ITag[] | string[],
    displayName: string,
    onCheckboxChange: () => void,
    onTagClick: (_tag: string) => void,
    onIncidentsClick: MonitorRowClickHandler,
    onLogClick: MonitorRowClickHandler,
    onEditClick: MonitorRowClickHandler,
    onPauseClick: MonitorRowClickHandler,
    onResumeClick: MonitorRowClickHandler,
    onDeleteClick: MonitorRowClickHandler,
    onTagsClick: MonitorRowClickHandler,
    onToggleClick: (_event: MouseEvent<HTMLDivElement>) => Promise<void>,
    onCheckNow: () => Promise<void>,
}

export const useHttpMonitorRow = (item: IHttpMonitorOutput): IUseHttpMonitorRow => {
    const {activeModal, setActiveModal, closeModal, openModal} = useMonitorRowState<HttpMonitorModalType>();
    const {isSelected, onCheckboxChange} = useMonitorRowSelection(item.uuid, httpMonitorSelectedIdsSelector, toggleOne);

    const navigate = useNavigate();
    const onTagClick = useToggleTagFilter();
    const canCreate = useCanCreateHttpMonitor();

    const navigateTo = (url: string): MonitorRowClickHandler => (event) => {
        event.stopPropagation();
        navigate(generatePath(url, {monitorId: item.uuid}));
    };

    const onToggleClick = useHttpMonitorToggle(item, useCallback(() => setActiveModal('limit'), [setActiveModal]));
    const onCheckNow = useHttpMonitorCheckNow(item.uuid);

    return {
        activeModal,
        closeModal,
        canCreate,
        isSelected,
        isPaused: item.status === 'paused',
        tags: item.tags || [],
        displayName: getHttpMonitorDisplayName(item.name),
        onCheckboxChange,
        onTagClick,
        onIncidentsClick: navigateTo(AppUrls.HTTP_MONITOR_DETAILS),
        onLogClick: navigateTo(AppUrls.HTTP_MONITOR_LOGS),
        onEditClick: openModal('edit', YM_GOALS.EDIT_MONITOR),
        onPauseClick: openModal('pause', YM_GOALS.PAUSE_MONITOR),
        onResumeClick: openModal('resume'),
        onDeleteClick: openModal('delete', YM_GOALS.DELETE_MONITOR),
        onTagsClick: openModal('tags'),
        onToggleClick,
        onCheckNow,
    };
};

export default useHttpMonitorRow;
